import { ConfigService } from './../../../../formbird-services/src/lib/services/config/config.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ComponentDefinition } from '@formbird/types';
import { UtilArray, UtilDocumentId } from '@formbird/shared';
import { Lightbox } from 'ngx-lightbox';
import { FormbirdInjectorService } from '../../../../formbird-services/src/lib/services/injector/formbird-injector.service';
import { ComponentSetupService } from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import { ChangedDocumentService } from 'formbird-services';
import { AbstractComponent } from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import * as isEqual from 'lodash/isEqual';
import { sclHandlebars } from '../../../../formbird-sc-shared/src/lib/handlebars/sclHandlebars';

// extend the ComponentDefinition type with the additional fields for the component
interface UploaderComponentDefinition extends ComponentDefinition {
  fileReferenceTemplateId: any;
  lightboxOptions?: any;
  maxImageHeight?: number;
  handlebarsHelpers?: any;
  fileTypes?: string[];
  maxFiles?: number;
  getEXIF?: boolean;
  writeEXIFToDoc?: string;
  writeEXIFToFile?: {
    EXIFFieldName: string;
    value: string;
  }[];
  writeToImage: {
    textPosition: number[];
    font: string;
    fontSize: string;
    fontWeight: string;
    color: string;
    background: string;
    backgroundOpacity: number;
    value: string;
  }[];
  linkTargetOutput: string;
  useFullLink: string;
  linkTargetHandlebars: string;
}

@Component({
  templateUrl: './uploader.component.html',
  styleUrls: ['./uploader.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UploaderComponent extends AbstractComponent<UploaderComponentDefinition> implements OnInit {

  @ViewChild('imageCanvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @Input() account: any;
  selectedFiles = [];
  documentFiles = [];
  tobeDelete = [];

  allowDeletion = true;

  progress: number;

  private notificationService: any;
  private documentSaveService: any;
  private modifiedFieldService: any;
  private fileProviderService: any;
  private imageCompressionService: any;
  private imageGalleryService: any;
  private modalService: any;

  private fileExtensions = null;
  countFileSelect = 0;
  countFileStartCompress = 0;
  countFileTransferring = 0;
  noteUploading = '';
  noteStartCompress = '';
  noteTransferring = '';
  countTotalFile = 0;
  waitId = null;
  showEmptyThumbnail = false;
  listEmThum = [];
  statusNote = 'Or drop file here';
  maxUploadLimit = 0;
  contextParams: any;
  disableCompression = false;
  extensionWhiteList: string[];

  constructor(
    private configService: ConfigService,
    protected formbirdInjectorService: FormbirdInjectorService,
    private lightbox: Lightbox,
    protected changedDocumentService: ChangedDocumentService,
    protected componentSetupService: ComponentSetupService,
    protected cdRef: ChangeDetectorRef
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdRef);

    this.notificationService = this.formbirdInjectorService.get('NotificationService');
    this.documentSaveService = this.formbirdInjectorService.get('DocumentSaveService');
    this.modifiedFieldService = this.formbirdInjectorService.get('ModifiedFieldService');
    this.fileProviderService = this.formbirdInjectorService.get('FileProviderService');
    this.imageCompressionService = this.formbirdInjectorService.get('ImageCompressionService');
    this.imageGalleryService = this.formbirdInjectorService.get('ImageGalleryService');
    this.modalService = this.formbirdInjectorService.get('ModalService');
  }

  ngOnInit() {

    this.initialize();

    this.documentFiles = this.document[this.fieldName] || [];
    this.maxUploadLimit = this.componentDefinition.maxFiles ?? 0;
    this.extensionWhiteList = this.componentDefinition.fileTypes ??
      this.configService.clientConfig().fileUploader?.extensionWhitelist ?? [];

    sclHandlebars.initHandlebars(this.componentDefinition.handlebarsHelpers);

    this.statusNote = this.generateNote();

    // Disable Compression
    if( this.componentDefinition.getEXIF ) {
      this.disableCompression = true;
    }

    this.contextParams = {
      document: this.document,
      doc: this.document,
      account: this.account, // the account document from scope
      tpl: this.template,
      template: this.template,
      tplItem: this.componentDefinition, // For BackCompatibility
      componentDefinition: this.componentDefinition // the current components configuration details
    };

    super.ngOnInit();
    this.watchDocument();
  }

  onFieldValueChanged(fieldValue) {
    if (isEqual(this.fieldValue, fieldValue)) {
      return;
    }

    this.fieldValue = fieldValue;
    this.selectedFiles = [];
    if (fieldValue !== undefined) {
      for (const each of fieldValue) {
        this.addToFileArray(each);
      }
    }
    this.documentFiles = this.fieldValue || [];

    this.formControl.patchValue(this.fieldValue, { emitEvent: false, onlySelf: true });

    this.detectChanges();
  }

  initialize() {
    if (!this.documentHasData()) {
      this.selectedFiles = [];
      return;
    }

    for (const each of this.document[this.fieldName]) {
      // this.addToFileArray(each);
      if (each.deleted) {
        continue;
      }
      const file: any = {};
      file.fileNo = each.fileNo; // main identifier for the file
      file.fileName = each.fileName;
      file.fileType = each.fileType;
      file.fileText = each.fileText;
      file.basePath = each.basePath;
      if (each.fileURL !== undefined) {
        if (each.fileURL.search(/^http[s]*:\/\//) < 0) {
          file.fileURL = 'api/getFile/' + each.fileURL;
        } else {
          file.fileURL = each.fileURL;
        }
      }

      if (this.tobeDelete.indexOf(file.fileNo) > -1) {
        file.mode = 'TO_REMOVE'; // set this file to bie viewable in the confirmation delete view
      } else {
        file.mode = 'VIEW'; // set this file to be viewable in the view
      }

      file.isImage = this.isImageFile(file);
      // NOTE : If the ff algo was not placed as a function, a weird context bug will occur
      this.initFileResources(file);

      let idx = -1;
      if (this.selectedFiles != null && this.selectedFiles.length > 0) {
        for (let n = 0; n < this.selectedFiles.length; n++) {
          if (this.selectedFiles[n].fileNo === each.fileNo) {
            idx = n;
            break;
          }
        }
      }
      if (idx < 0) {
        this.selectedFiles.push(file); // register to main file array
      } else {
        this.selectedFiles[idx] = file;
      }
    }

    // tslint:disable-next-line:no-string-literal
    if (this.document['allowDeletion'] !== undefined) {
      // tslint:disable-next-line:no-string-literal
      this.allowDeletion = this.document['allowDeletion'];
    }

  }

  private addToFileArray(fileRecord) {
    // don't add files that either already exist or are deleted
    if (!fileRecord.deleted && this.selectedFiles.find(it => it.fileNo === fileRecord.fileNo) === undefined) {
      const file: any = {};
      file.fileNo = fileRecord.fileNo; // main identifier for the file
      file.fileName = fileRecord.fileName;
      file.fileType = fileRecord.fileType;
      file.fileText = fileRecord.fileText;
      file.fileURL = fileRecord.fileURL;
      file.mode = 'VIEW'; // set this file to be viewable in the view
      file.isImage = this.isImageFile(file);
      // NOTE: If the ff algo was not placed as a function, a weird context bug will occur - john.
      this.initFileResources(file);
      this.selectedFiles.push(file); // register to main file array
    }
  }

  documentHasData() {
    return this.document && this.document[this.fieldName] && this.document[this.fieldName].length;
  }

  initFileResources(file) {
    // direct link to the file. Load either from offline or from the server:
       this.fileProviderService.getFileResources(file).then((resources) => {
        file.resources = resources;
        file.fileURL = file.resources.url;
        if (file.isImage) {
          this.imageGalleryService.addImageToGallery(resources.url);
        }
        this.cdRef.detectChanges();
       });
    }

  viewImage(file) {
    const lightboxOptions = this.componentDefinition.lightboxOptions || {};

    if (file.isImage && file.resources) {
      const images = this.getImageFiles().filter(it => !(it instanceof File));
      this.lightbox.open(images.map((f: any) => {
        return {
          src: f.resources.base64strData || f.resources.url,
          caption: f.name || f.fileName,
          thumb: f.resources.url
        }
      }), images.indexOf(file), lightboxOptions);
    }
  }


  getNonImageFiles() {
    return this.selectedFiles.filter((each) => !each.isImage);
  }

  getImageFiles() {
    return this.selectedFiles.filter((each) => each.isImage);
  }

  private addPendingOperation(operationName, options) {
    const pendingOperationService = this.formbirdInjectorService.get('PendingOperationService');

    if (pendingOperationService) {
      pendingOperationService.addPendingOperation(operationName, options);
    } else {
      console.warn('Component cannot mark uploads as complete before save. Please update the core to 4.2 or higher.');
    }
  }

  private markOperationComplete(operationName, options) {
    const pendingOperationService = this.formbirdInjectorService.get('PendingOperationService');

    if (pendingOperationService) {
      pendingOperationService.markOperationComplete(operationName, options);
    } else {
      console.warn('Component cannot mark uploads as complete before save. Please update the core to 4.2 or higher.');
    }
  }

  removeFile(file) {
    if (this.disabled) {
      this.notificationService.printMessage('Unable to remove file attachment. Readonly access.', 'warning');
    } else {
      this.selectedFiles = this.selectedFiles.filter(f => file.fileNo !== f.fileNo);
      this.documentFiles = this.documentFiles.filter(f => file.fileNo !== f.fileNo);
      this.statusNote = this.generateNote();
      this.valueChanged(this.documentFiles);
    }
  }

  confirmDelete(file) { // NOTE *** : is this function missing in angular version
    const idx = this.selectedFiles.indexOf(file);
    if (this.selectedFiles[idx].mode === 'VIEW') {
      this.tobeDelete = [];
      this.selectedFiles.map((val, i) => {
        this.selectedFiles[i].mode = 'VIEW';
      });
      this.tobeDelete.push(this.selectedFiles[idx].fileNo);
      this.selectedFiles[idx].mode = 'TO_REMOVE';
    } else {
      const fileIdx = this.tobeDelete.indexOf(this.selectedFiles[idx].fileNo);
      this.tobeDelete.splice(fileIdx, 1);
      this.selectedFiles[idx].mode = 'VIEW';
    }
  }

  generateNote(){
    let statusNote = 'Or drop file here';
    if(this.componentDefinition.maxFiles){
      statusNote = `Or Drop here ${this.documentFiles.length ?? 0}/${this.componentDefinition.maxFiles}`;
    }

    return statusNote;
  }

  resetNote() {
    this.listEmThum = [];
    this.countFileSelect = 0;
    this.countFileStartCompress = 0;
    this.countFileTransferring = 0;
    this.noteStartCompress = '';
    this.noteUploading = '';
    this.noteTransferring = '';
    this.countTotalFile = 0;
  }

  async onFileSelect(files) {
    if(this.componentDefinition?.fileTypes && (this.componentDefinition?.fileTypes?.length ?? 0) === 0)
    {
      this.modalService.showDialog('Accepted File Types should be set', 'ERROR');
      return;
    }

    if (this.disabled || !files ||
        files.length === 0 || (this.countTotalFile > 0 && this.countFileTransferring > 0)) {
      return;
    }

    if(((this.documentFiles.length + files.length) > this.maxUploadLimit && this.maxUploadLimit > 0)){
      this.modalService.showDialog(this.maxUploadLimit + ' Maximum Upload limit reach', 'ERROR');
      return;
    }

    if (files.detail) { // for use with sc-pdf upload configuration as per # 17897
      files = files.detail;
      if (!files || files.length === 0 || files[0] === undefined) { return; }
    }

    this.resetNote();
    this.countTotalFile = files.length;
    this.statusNote = '';

    if (this.waitId != null) {
      clearInterval(this.waitId);
    }

    const invalidFiles = [];
    const validFiles = this.componentDefinition.fileTypes ? [] : [...files];

    if(this.componentDefinition.fileTypes){
      for (const f of files) {
        const type = f.type.split('/')[1] ?? '';
        if( this.componentDefinition.fileTypes.includes(type) || !this.componentDefinition.fileTypes){
          validFiles.push(f);
        }else{
          invalidFiles.push(f);
          console.warn(`Filtered file ${f.name}. Type '${f.type}' not allowed`)
        }
      }
    }

    for (const validFile of validFiles) {
      let file = validFile;
      if( this.isImageFile(file) && this.componentDefinition.getEXIF){
        try{
          file = await this.processExif(file);
        }catch(e){
          file.noExifSupport = true;
          console.warn('Invalid Image. Not supported EXIF');
        }
      }
      this.upload(file, validFiles.length);
    }

    if(invalidFiles.length > 0){
      let errMsg = 'The following files were NOT uploaded as they have Invalid Extensions<br />';

      errMsg += '<ul class="upload-modal-error">';
      invalidFiles.map((file) => {
        errMsg += `<li style="text-align:left;">${file.name}</li>`;
      });
      errMsg += '</ul>';

      this.modalService.showDialog(errMsg, 'Warning');
      this.resetNote();
      this.statusNote = this.generateNote();
    }
  }

  async processExif(file: File): Promise<File>{
      const piexif = await this.imageCompressionService.getPiexifjs();
      const base64 = await this.readFileAsDataURL(file);
      const exifObj = piexif.load(base64);
      const { updatedBase64, updatedExifData } = this.appendExif(base64, exifObj, piexif);
      return this.imageCompressionService.dataURLtoFile(updatedBase64, file.name);
  }

  readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }

  appendExif(base64Image: string, exifObj: any, piexif: any): { updatedBase64: string; updatedExifData: any }  {
    const exifData = this.formatExif(exifObj, piexif);

    const exifMap = {
      Make: { group: '0th', path: piexif.ImageIFD.Make },
      Model: { group: '0th', path: piexif.ImageIFD.Model },
      Software: { group: '0th', path: piexif.ImageIFD.Software },
      DateTime: { group: '0th', path: piexif.ImageIFD.DateTime },
      DateTimeOriginal:{ group: 'Exif', path: piexif.ExifIFD.DateTimeOriginal},
      DateTimeDigitized:{ group: 'Exif', path: piexif.ExifIFD.DateTimeDigitized},
      GPSLatitude:{ group: 'GPS', path: piexif.GPSIFD.GPSLatitude},
      GPSLongitude:{ group: 'GPS', path: piexif.GPSIFD.GPSLongitude},
      GPSAltitude:{ group: 'GPS', path: piexif.GPSIFD.GPSAltitude},
      GPSLatitudeRef:{ group: 'GPS', path: piexif.GPSIFD.GPSLatitudeRef},
      GPSLongitudeRef:{ group: 'GPS', path: piexif.GPSIFD.GPSLongitudeRef},
    };

    // exifObj['0th'][piexif.ImageIFD.Make] = 'AngularCam';
    // exifObj['0th'][piexif.ImageIFD.Model] = 'EXIFixer 9000';

    const writeToExif = this.componentDefinition.writeEXIFToFile ?? [];
    const newExifData = {};
    for( const exif of writeToExif){

      const exifValue = sclHandlebars.processHandlebars(
        exif.value,{...this.contextParams, exif: { ...exifData['0th'], ...exifData.Exif}}, null);

      if( exifMap[exif.EXIFFieldName] ){
        const exifLine = exifMap[exif.EXIFFieldName];
        exifObj[exifLine.group][exifLine.path] = exifValue;

      }else{
        const exifName = sclHandlebars.processHandlebars(
          exif.EXIFFieldName,{...this.contextParams, exif: { ...exifData['0th'], ...exifData.Exif}}, null);

        newExifData[exifName] = exifValue
      }

    }

    if( Object.keys(newExifData).length > 0 )
      exifObj.Exif[piexif.ExifIFD.UserComment] = JSON.stringify(newExifData);

    const exifBytes = piexif.dump(exifObj);
    const updatedBase64 = piexif.insert(exifBytes, base64Image);

    return { updatedBase64, updatedExifData: exifObj};
  }

  upload(file, len) {
    this.countFileSelect++;
    this.listEmThum.push(file);
    this.showEmptyThumbnail = true;
    this.noteUploading = 'Uploading ' + this.countFileSelect + ' file(s)';
    this.statusNote = this.noteUploading;


    const fileNo = UtilDocumentId.generateId();
    const documentId = this.document.documentId;
    const documentName = this.document.systemHeader.summaryName;

    this.addPendingOperation(fileNo, {
      documentId: this.document.documentId,
      callerName: this.fieldName
    });

    const doUpload = () => {
      this.disabled = true;
      this.cdr.detectChanges();
      this.countFileTransferring++;
      this.noteTransferring = 'Transferring ' + this.countFileTransferring + ' file(s)';
      let note = this.noteUploading + ' / ' + this.noteStartCompress + ' / ' + this.noteTransferring;
      if (this.countFileTransferring === this.countTotalFile) {
        note = this.noteUploading + ' / ' + this.noteTransferring;
      }

      this.statusNote = note;

      file.fileNo = fileNo;
      file.fileName = file.name;

      const lastDotIndex = file.name.lastIndexOf('.'); // for files without extensions
      if (lastDotIndex === -1) {
        file.fileType = 'UNKNOWN';
      } else {
        file.fileType = file.name.substring(lastDotIndex + 1).toUpperCase();
      }

      file.progress = 0;
      this.selectedFiles.push(file);
      this.cdRef.detectChanges();
      // console.log('****** this.selectedFiles = ', this.selectedFiles);

      // get Exif from file then remove it to lessen the size of uploadData
      const fileExif = file.exif ?? null;
      if(file.exif) delete file.exif;


      const uploadData: any = {
        fileNo: fileNo,
        file: file,
        documentId: documentId,
        documentName: documentName,
        docFieldName: this.fieldName,
      };
      if (!(typeof this.componentDefinition.fileReferenceTemplateId === 'undefined' ||
        this.componentDefinition.fileReferenceTemplateId === null)) {
        uploadData.fileReferenceTemplateId = this.componentDefinition.fileReferenceTemplateId;
      }

      this.fileProviderService.uploadFile(uploadData).subscribe(
        (event) => {
          if (event.body && event.status === 200) {
            this.markOperationComplete(fileNo, {
              documentId: this.document.documentId,
              callerName: this.fieldName
            });

            this.countFileTransferring--;
            if (this.countFileTransferring > 0) {
              this.statusNote = 'Uploading ' + this.countFileSelect + ' file(s) / Transferring ' + this.countFileTransferring + ' file(s)';
            }
            if (this.countFileTransferring === 0) {
              this.statusNote = 'Uploading ' + this.countFileSelect + ' file(s) - Ready to upload More or Save';
              this.disabled = false;
              this.cdr.detectChanges();
              this.wait();
            }


            const fileRecord: any = {
              fileName: file.name,
              fileType: event.body.fileType,
              fileNo: event.body.fileNo,
              basePath: event.body.basePath,
            };

            if( this.componentDefinition.writeEXIFToDoc && fileExif){
              fileRecord[this.componentDefinition.writeEXIFToDoc] = {
                exif: fileExif
              }
            }

            const index = UtilArray.containsIndex(this.documentFiles, fileRecord, function (item1, item2) {
              return item1.fileNo === item2.fileNo;
            });
            if (index >= 0) {
              this.documentFiles[index] = fileRecord;
            } else {
              this.documentFiles = [...this.documentFiles, fileRecord];
            }
            this.valueChanged(this.documentFiles);

            for (let i = 0; i < this.selectedFiles.length; i++) {
              if (this.selectedFiles[i].fileNo === fileNo) {
                if (!fileRecord.deleted) {
                  const aFile: any = {};
                  aFile.fileNo = fileRecord.fileNo; // main identifier for the file
                  aFile.fileName = fileRecord.fileName;
                  aFile.fileType = fileRecord.fileType;
                  aFile.fileText = fileRecord.fileText;
                  aFile.mode = 'VIEW'; // set this file to be viewable in the view
                  aFile.isImage = this.isImageFile(aFile);
                  aFile.basePath = fileRecord.basePath;
                  aFile.fileURL = `api/getFile/${fileRecord.fileNo}`;
                  aFile.name = fileRecord.fileName;
                  // NOTE: If the ff algo was not placed as a function, a weird context bug will occur - john.
                  this.initFileResources(aFile);
                  this.selectedFiles[i] = aFile; // register to main file array
                }
                break;
              }
            }
          } else {
            if (event.loaded && event.total) {
              const p = (event.loaded / event.total) * 100;
              if (file.progress !== p) {
                file.progress = p;
                this.cdRef.detectChanges();
                // console.log('file progress = ' + file.progress);
              }

              if (p >= 100) {
                this.markOperationComplete(fileNo, {
                  documentId: this.document.documentId,
                  callerName: this.fieldName
                });
              }
            }
          }
        },
        (err) => {
          file.progress = -1;

          this.markOperationComplete(fileNo, {
            documentId: this.document.documentId,
            callerName: this.fieldName
          });

          // remove the file and its progress from view
          this.selectedFiles.splice(this.selectedFiles.indexOf(file), 1);
          this.resetNote();
          this.statusNote = this.generateNote();
          this.cdRef.detectChanges();
          this.modalService.showDialog(err.error || err.message, 'ERROR');
          this.disabled = false;
          this.cdr.detectChanges()
        }
      );
    };

    file.isImage = this.isImageFile(file);
    if (file.isImage) {
      const reader = new FileReader();

      reader.addEventListener('load', () => {
        file.data = reader.result; // base64StrData

        if (this.isCompressibleImageFile(file)) {
          const image = document.createElement('img');
          image.src = file.data;

          this.countFileStartCompress++;
          this.noteStartCompress = 'Processing ' + this.countFileStartCompress + ' file(s)';
          this.statusNote = this.noteUploading + ' / ' + this.noteStartCompress;

          image.onload = async () => {
            if(this.componentDefinition.getEXIF && !file?.noExifSupport){
              const { base64, resizedFile, exif } = await this.resizeImageExif(file, this.componentDefinition.maxImageHeight);
              file = resizedFile;
              file.isImage = true;
              file.exif = exif;

              if (this.listEmThum.length === len) {
                this.showEmptyThumbnail = false;
              }

              doUpload();

            }else{
              this.imageCompressionService.resizeImage(file, this.componentDefinition.maxImageHeight).then(
                async (base64Str: string) => {
                  file = this.imageCompressionService.dataURLtoFile(base64Str, file.name);
                  file.isImage = true;

                  if (this.listEmThum.length === len) {
                    this.showEmptyThumbnail = false;
                  }

                  doUpload();
                },
                (err) => {
                  console.error('Image onload error: ', err);
                }
              );
            }

          };

          image.onerror = err => {
            console.error('Image onload error.: ', err);
            this.notificationService.error('Error reading file ' + file.name);
          };
        } else {
          doUpload();
        }
      }, false);
      // Read in the image file as a data URL.
      reader.readAsDataURL(file);
    } else {
      doUpload();
    }
  }

  writeToImageHandler(canvas, h, w, ctx, exifData: any, textDetails){
    const padding = 5;
    const ctxFont = [];

    const waterMarkedText = sclHandlebars.processHandlebars(
      textDetails.value,
      {...this.contextParams, exif: { ...exifData['0th'], ...exifData.Exif}},
      null
    );

    ctxFont.push(textDetails?.fontWeight ?? null);
    ctxFont.push(textDetails?.fontSize ?? '24px');
    ctxFont.push(textDetails?.font ?? 'sans-serif');
    ctx.font = ctxFont.filter(n => n).join(' ');

    const watermarkText = waterMarkedText || '';
    const metrics = ctx.measureText(waterMarkedText);
    const textWidth = metrics.width;
    const textHeight = metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
    // const actualHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
    // const boxHeight = textHeight;
    let boxHeight = textHeight;
    const extraSpaceFix = textHeight < 30 ? 0 : textHeight * 0.175;
    const boxWidth = textWidth + (padding * 2);

    let textX = 0;
    let textY = 0;
    let boxX = 0;
    let boxY = 0;

    const [posX = -5, posY = 5] = textDetails?.textPosition;

    if(posX < 0 ){
      ctx.textAlign = 'right';
      textX = w - padding - Math.abs(posX);
      boxX = w - boxWidth - Math.abs(posX);
    }else if( posX > 0 ){
      ctx.textAlign = 'left';
      textX = padding + posX;
      boxX =  posX;
    }else{
      ctx.textAlign = 'center';
      textX = canvas.width / 2;
      boxX = canvas.width / 2 - boxWidth/2;
    }

    if( posY < 0 ){
      ctx.textBaseline = 'top';
      boxHeight = textHeight;
      textY = Math.abs( posY ) + padding;
      boxY = Math.abs( posY );
    }else if( posY > 0 ){
      ctx.textBaseline = 'bottom';
      boxHeight = textHeight - extraSpaceFix;
      textY = h  - padding - posY + extraSpaceFix;
      boxY  = h - boxHeight - posY;
    }else{
      ctx.textBaseline = 'middle'
      textY = (h / 2) - (textHeight / 2);
      boxY = (h / 2) - boxHeight;
    }


    // Add a background highlight to the text
    ctx.fillStyle = textDetails?.background ?? 'none';
    ctx.globalAlpha = textDetails?.backgroundOpacity ?? 1; // Fully opaque for the highlight
    ctx.fillRect(boxX, boxY, boxWidth , boxHeight);

    // Add watermark

    ctx.globalAlpha = 1;
    ctx.fillStyle = textDetails?.color ?? 'rgba(255, 255, 255, 1)';
    ctx.fillText(watermarkText, textX, textY);
  }

  async resizeImageExif(file: File, maxHeight: number): Promise<{ base64: string; resizedFile: File; exif: any }> {
    const piexif = await this.imageCompressionService.getPiexifjs();
    const base64 = await this.readFileAsDataURL(file);

    const originalExif = piexif.load(base64);
    const exifData = this.formatExif(originalExif, piexif);
    // const waterMarkedText = sclHandlebars.processHandlebars(
    //   this.componentDefinition.writeToImage.value,
    //   {...this.contextParams, exif: { ...exifData['0th'], ...exifData.Exif}},
    //   null
    //   );

    const img = await this.loadImage(base64);
    const { w, h } = this.imageCompressionService.getResizedDimensions(img, maxHeight);


    // Draw resized image onto canvas
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('2D canvas context not supported.');
    }
    const orientation = originalExif['0th'][piexif.ImageIFD.Orientation] || 1;

    // Adjust canvas based on EXIF orientation
    if(orientation === 6){
      canvas.width = h;
      canvas.height = w;
      ctx.rotate(-0.5 * Math.PI);
      ctx.translate(-w, 0);
    }

    switch (orientation) {
          case 3: // 180° rotate
            ctx.translate(w, h);
            ctx.rotate(Math.PI);
            break;
          case 6: // Rotated 90° CW --- To fix need to rotate CCW
            canvas.width = h;
            canvas.height = w;
            ctx.rotate(-0.5 * Math.PI);
            ctx.translate(-w, 0);
            break;
          case 8: // Rotated 90° CCW --- To fix need to rotate CW
            canvas.width = h;
            canvas.height = w;
            ctx.rotate(0.5 * Math.PI);
            ctx.translate(0, -h);
            break;
          default:
            // No rotation needed
            break;
        }

    ctx.drawImage(img, 0, 0, w, h);

    const writeToImageArray = Array.isArray(this.componentDefinition.writeToImage) ?
        this.componentDefinition.writeToImage :
      [this.componentDefinition.writeToImage];

    writeToImageArray.map((writeToImage) => {
      this.writeToImageHandler(canvas, h, w, ctx, exifData, writeToImage);
    });


    // Get resized base64 (without EXIF)
    const resizedBase64 = canvas.toDataURL(file.type, 0.92);

    // Re-insert EXIF
    const exifBytes = piexif.dump(originalExif);
    const finalBase64 = piexif.insert(exifBytes, resizedBase64);

    // Convert to File if needed
    const resizedFile = this.base64ToFile(finalBase64, file.name);

    return {
      base64: finalBase64,
      resizedFile: resizedFile,
      exif: originalExif,
    };
  }

  base64ToFile(base64: string, filename: string): File {
    const [meta, data] = base64.split(',');
    const mime = meta.match(/:(.*?);/)[1];
    const binary = atob(data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    return new File([array], filename, {
      type: mime,
      lastModified: Date.now(),
    });
  }

  private formatExif(rawExif: any, piexif: any): any {
    // Convert tag values into a more readable object
    const decoded: any = {};
    for (const ifd in rawExif) {
      if (ifd === 'thumbnail') continue; // Skip thumbnail binary
      decoded[ifd] = {};
      for (const tag in rawExif[ifd]) {
        if ( rawExif[ifd][tag] ){
          const tagName = piexif.TAGS[ifd]?.[tag]?.name || tag;
          decoded[ifd][tagName] = rawExif[ifd][tag];
        }
      }
    }
    return decoded;
  }

  loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
      img.src = src;
    });
  }

  wait() {
    this.waitId = setTimeout(() => {
      this.statusNote = this.generateNote();
      this.countTotalFile = 0;
      this.cdRef.detectChanges();
      // console.log('****** done waiting');
    }, 5000);// wait for 5 second
  }

  drag(event) {
    event.dataTransfer.setData('fileIndex', event.target.id);
  }

  getNonImageFileIcon(file) {
    const name = file.name || file.fileName;
    let ext;
    if (name) {
      ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase();
      if (ext.startsWith('doc') || ext.startsWith('word')) {
        ext = 'docx';
      } else if (ext.startsWith('xls') || ext.startsWith('excel')) {
        ext = 'xls';
      } else if (ext.startsWith('ppt')) {
        ext = 'ppt';
      } else if (ext.endsWith('zip') || ext.startsWith('rar')) {
        ext = 'zip';
      }
    }
    return 'images/ft-uploader/ft-uploader-' + ext + '-32.png';
  }

  isImageFile(file) {
    const name = file.name || file.fileName;
    if (name) {
      const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase();
      if (this.fileExtensions == null) {
        this.fileExtensions = this.configService.clientConfig().imageViewer.supportedExtensions;
      }
      return this.fileExtensions.indexOf(ext) > -1;
    }
    return false;
  }

  isCompressibleImageFile(file) {
    let cExts = null;

    const name = file.name || file.fileName;
    if (name) {
      const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase();
      if (cExts == null) {
        cExts = this.configService.clientConfig().imageViewer?.supportedExtensions || [];
      }

      if (ext.indexOf('tif') > -1) {
        return false;
      } else {
        return cExts.indexOf(ext) > -1;
      }
    }

    return false;
  }

  saveDocument(strMessage) {
    const unsavedDocumentId = this.formParameters.unsavedDocumentListId;
    this.modifiedFieldService.addModifiedField(this.document.documentId, this.fieldName, unsavedDocumentId, false);
    this.notificationService.printMessage(strMessage, 'success');
    this.documentSaveService.save(unsavedDocumentId);
  }

  download(file) {
    this.fileProviderService.downloadFile(file);
  }

  showToolTip(tooltip: any, formFieldContainer: any, labelContainer: any) {
    return (labelContainer.offsetWidth <= formFieldContainer._elementRef.nativeElement.scrollWidth);
  }

  toggleTooltip(tooltip: any) {
    setTimeout(() => {
      tooltip.toggle();
    });
  }

  watchDocument() {
    const createWatchDocument = () => {
      const watchDoc = this.changedDocumentService.watchDocument(this.document.documentId).subscribe((newValue: any) => {
        this.contextParams.document = newValue;
      });

      this.subs.add(watchDoc);
    };

    createWatchDocument();
  }

  getLink( file: any ){

    const linkTargetOutput = this.componentDefinition.linkTargetOutput ?? '';
    const useFullLink = this.componentDefinition.useFullLink ?? false;

    const filePath = file.fileURL.replace(/^\/+/, '');
    const url = useFullLink ? `${window.location.origin}/${filePath}` : filePath;

    if( linkTargetOutput === 'markdown'){
      return `${file.isImage ? '!' : ''}[${file.fileName}](${url})`;
    }
    else if( linkTargetOutput === 'html' ) {
      return file.isImage ? `<img alt="${file.fileName}" src="${url}" style="width: 400px;">` : url;
    }
    else if( linkTargetOutput === 'url' ) {
      return url;
    }
    else if( linkTargetOutput === 'handlebars' ) {
      const tmpl = this.componentDefinition.linkTargetHandlebars ?? '';
      try{
        const context = {
            uploader: {
              current: file,
              fullPath: window.location.origin,
            },
        };
        console.info('Handlebar Context: ', JSON.stringify(context));

        return sclHandlebars.processHandlebars(
          tmpl,
          context,
          {}
        );
      }catch(e){
        return e;
      }
    }
    else{
      return url;
    }
  }

  copyToClipboard(file: any, event: MouseEvent) {
    const text= this.getLink(file);
    const btn = event.currentTarget as HTMLElement | null;
    if (!btn) return;

    navigator.clipboard.writeText(text).then(() => {

      const copyIcon = btn.querySelector('.copy-icon');
      const checkIcon = btn.querySelector('.check-icon');
      if (!copyIcon || !checkIcon) return;

      // Add a class to animate
      copyIcon.classList.remove('active');
      checkIcon.classList.add('active');

      setTimeout(() => {
        checkIcon.classList.remove('active');
        copyIcon.classList.add('active');
      }, 1000);
    });
  }
}
